import { Fragment } from "react";
import type { PublicCalendarEntry } from "@/api/public";
import { isWeekendOrHolidayBW } from "@/lib/holidaysBW";

const MONTHS_DE = [
  "Januar","Februar","März","April","Mai","Juni",
  "Juli","August","September","Oktober","November","Dezember",
];

const DOW_SHORT = ["S","M","D","M","D","F","S"];

type Props = {
  year: number;
  month: number; // 0-based
  unitNames: string[];
  entries: PublicCalendarEntry[];
  today?: Date;
  cellWidth?: number;
  rowHeight?: number;
};

function daysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function formatDe(iso: string): string {
  if (!iso || iso.length < 10) return iso;
  const [y, m, d] = iso.slice(0, 10).split("-");
  return `${d}.${m}.${y}`;
}

function parseIso(s: string): Date {
  const [y, m, d] = s.slice(0, 10).split("-").map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1);
}

const UNIT_COLOR_MAP: Record<string, string> = {
  EG: "var(--unit-eg)",
  OG: "var(--unit-og)",
  Zeltplatz: "var(--unit-zp)",
  DG: "var(--unit-dg)",
  LZ: "var(--unit-lz)",
};

function unitColor(name: string): string {
  return UNIT_COLOR_MAP[name] ?? "var(--brand-700)";
}

// Halbtag-Logik analog calendarOccupancy.ts: Anreisetag ab Nachmittag, Abreisetag bis Vormittag.
// Für die Balken-Darstellung wird daraus eine fraktionale Position innerhalb der Tagessäule.
function barFractions(
  e: PublicCalendarEntry,
  monthStart: Date,
  monthEnd: Date,
): { startCol: number; endCol: number; startFrac: number; endFrac: number } | null {
  const cin = parseIso(e.check_in);
  const cout = parseIso(e.check_out);
  if (isNaN(cin.getTime()) || isNaN(cout.getTime())) return null;
  if (cout.getTime() <= cin.getTime()) return null;

  const ciTime = (e.check_in_time ?? "15:00").slice(0, 5);
  const coTime = (e.check_out_time ?? "10:00").slice(0, 5);
  const ciHour = parseInt(ciTime.split(":")[0] || "15", 10);
  const ciMin = parseInt(ciTime.split(":")[1] || "0", 10);
  const coHour = parseInt(coTime.split(":")[0] || "10", 10);
  const coMin = parseInt(coTime.split(":")[1] || "0", 10);
  const ciFrac = (ciHour * 60 + ciMin) / (24 * 60);
  const coFrac = (coHour * 60 + coMin) / (24 * 60);

  // Begrenzung auf Monatsfenster
  const ms = monthStart;
  const me = new Date(monthEnd.getFullYear(), monthEnd.getMonth(), monthEnd.getDate());

  let startCol: number;
  let startFrac: number;
  if (cin < ms) {
    startCol = 1;
    startFrac = 0;
  } else if (cin.getFullYear() === ms.getFullYear() && cin.getMonth() === ms.getMonth()) {
    startCol = cin.getDate();
    startFrac = ciFrac;
  } else {
    return null; // außerhalb des Monats
  }

  let endCol: number;
  let endFrac: number;
  if (cout > me) {
    endCol = me.getDate();
    endFrac = 1;
  } else if (cout.getFullYear() === me.getFullYear() && cout.getMonth() === me.getMonth()) {
    endCol = cout.getDate();
    endFrac = coFrac;
  } else {
    return null;
  }

  return { startCol, endCol, startFrac, endFrac };
}

export function MonthStripBars({
  year,
  month,
  unitNames,
  entries,
  today,
  rowHeight = 22,
}: Props) {
  const num = daysInMonth(year, month);
  const days = Array.from({ length: num }, (_, i) => i + 1);
  const labelWidth = 36;
  const monthStart = new Date(year, month, 1);
  const monthEnd = new Date(year, month, num);
  const todayInView =
    !!today && today.getFullYear() === year && today.getMonth() === month;
  const barHeight = 10;

  // Ein gemeinsames Grid liefert die Tagesspalten für Header und Belegungszeilen.
  // Die Balken liegen als prozentuales Overlay über demselben Spaltenbereich,
  // damit Datum, Tageslinien und Balken auf jedem Gerät bündig bleiben.
  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: `${labelWidth}px repeat(${num}, minmax(0, 1fr))`,
      border: "1px solid var(--border-strong)",
      borderRadius: 8,
      background: "var(--bg-card)",
      overflow: "hidden",
      width: "100%",
    }}>
      <div style={{
        gridColumn: "1 / -1",
        background: "var(--brand-700)",
        color: "var(--fg-inverse)",
        padding: "6px 12px",
        fontFamily: "var(--font-display)",
        fontSize: 16,
        letterSpacing: ".02em",
      }}>
        {MONTHS_DE[month]} {year}
      </div>

      {/* Tag-Header: leeres Label */}
      <div style={{
        height: 28,
        background: "var(--bg-subtle)",
        borderRight: "1px solid var(--border-strong)",
        boxSizing: "border-box",
      }} />
      {days.map(d => {
        const date = new Date(year, month, d);
        const we = isWeekendOrHolidayBW(date);
        const dow = date.getDay();
        const isToday = todayInView && today!.getDate() === d;
        return (
          <div
            key={`h-${d}`}
            style={{
              height: 28,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 9,
              fontWeight: isToday ? 700 : 500,
              color: isToday ? "var(--fg-inverse)" : (we ? "var(--accent)" : "var(--fg-muted)"),
              background: isToday
                ? "var(--brand-700)"
                : (we ? "color-mix(in oklab, var(--accent) 8%, transparent)" : "var(--bg-subtle)"),
              borderRight: "1px solid var(--border-strong)",
              boxSizing: "border-box",
              lineHeight: 1.1,
            }}
          >
            <span style={{ fontSize: 8, opacity: 0.75 }}>{DOW_SHORT[dow]}</span>
            <span style={{ fontSize: 10 }}>{d}</span>
          </div>
        );
      })}

      {/* Einheit-Zeilen */}
      {unitNames.map(unit => {
        const unitEntries = entries.filter(e => e.einheit_name === unit);
        const color = unitColor(unit);
        return (
          <Fragment key={unit}>
            <div style={{
              height: rowHeight,
              padding: "0 6px",
              display: "flex",
              alignItems: "center",
              fontSize: 12,
              fontWeight: 600,
              color: "var(--fg)",
              background: "var(--bg-subtle)",
              borderRight: "1px solid var(--border-strong)",
              borderTop: "1px solid var(--border-strong)",
              boxSizing: "border-box",
            }}>
              {unit}
            </div>
            {/* Tagesbereich: spannt alle Tagesspalten, teilt deren Tracks via verschachteltem Grid */}
            <div style={{
              gridColumn: "2 / -1",
              position: "relative",
              height: rowHeight,
              borderTop: "1px solid var(--border-strong)",
              boxSizing: "border-box",
            }}>
              {/* Wochenend-/Tag-Hintergrund + senkrechte Tageslinien */}
              <div style={{
                position: "absolute",
                inset: 0,
                display: "grid",
                gridTemplateColumns: `repeat(${num}, minmax(0, 1fr))`,
              }}>
                {days.map(d => {
                  const date = new Date(year, month, d);
                  const we = isWeekendOrHolidayBW(date);
                  return (
                    <div
                      key={d}
                      style={{
                        borderRight: "1px solid var(--border)",
                        background: we ? "color-mix(in oklab, var(--accent) 8%, transparent)" : "transparent",
                        boxSizing: "border-box",
                      }}
                    />
                  );
                })}
              </div>

              {/* Heute-Linie */}
              {todayInView && (
                <div style={{
                  position: "absolute",
                  top: 0,
                  bottom: 0,
                  left: `${((today!.getDate() - 0.5) / num) * 100}%`,
                  width: 2,
                  background: "var(--brand-700)",
                  zIndex: 2,
                  pointerEvents: "none",
                }} />
              )}

              {/* Belegungs-Balken */}
              {unitEntries.map((e, idx) => {
                const frac = barFractions(e, monthStart, monthEnd);
                if (!frac) return null;
                const leftPct = ((frac.startCol - 1 + frac.startFrac) / num) * 100;
                const rightPct = ((frac.endCol - 1 + frac.endFrac) / num) * 100;
                const widthPct = Math.max(0.5, rightPct - leftPct);
                return (
                  <div
                    key={idx}
                    title={`Belegt: ${formatDe(e.check_in)} – ${formatDe(e.check_out)}`}
                    style={{
                      position: "absolute",
                      left: `${leftPct}%`,
                      width: `${widthPct}%`,
                      top: (rowHeight - barHeight) / 2,
                      height: barHeight,
                      background: color,
                      opacity: 0.85,
                      borderRadius: 3,
                      zIndex: 1,
                    }}
                  />
                );
              })}
            </div>
          </Fragment>
        );
      })}
    </div>
  );
}
